import axios, { AxiosError } from 'axios';
import { axiosApiInstancePrivate } from '..';
import { endpoints } from '../endpoints';
import { appendHsOwnerIdIfPresentAndAdmin } from '../../../utils/apiUtilis';

export interface GoogleCalendarAuthResponse {
  authUrl: string;
}

export interface GoogleCalendarStatusResponse {
  connected: boolean;
  expiryDate?: string;
}

export interface GoogleCalendarEvent {
  kind: string;
  etag: string;
  id: string;
  status: string;
  htmlLink: string;
  created: string;
  updated: string;
  summary: string;
  description?: string;
  creator: {
    email: string;
    self: boolean;
  };
  organizer: {
    email: string;
    self: boolean;
  };
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  location?: string;
  reminders: {
    useDefault: boolean;
    overrides?: Array<{
      method: string;
      minutes: number;
    }>;
  };
}

export interface GoogleCalendarEventsResponse {
  events: GoogleCalendarEvent[];
  count: number;
}

export interface GetGoogleCalendarEventsParams {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
}

/**
 * Inizia il flusso OAuth per connettere Google Calendar
 * @returns URL di autorizzazione Google
 */
export const getGoogleCalendarAuthUrl = async (): Promise<GoogleCalendarAuthResponse> => {
  try {
    const url = appendHsOwnerIdIfPresentAndAdmin(endpoints.googleCalendar.auth);
    const response = await axiosApiInstancePrivate.get<GoogleCalendarAuthResponse>(
      url
    );
    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

/**
 * Verifica lo stato della connessione con Google Calendar
 * @returns Stato della connessione e data di scadenza
 */
export const getGoogleCalendarStatus = async (): Promise<GoogleCalendarStatusResponse> => {
  try {
    const url = appendHsOwnerIdIfPresentAndAdmin(endpoints.googleCalendar.status);
    const response = await axiosApiInstancePrivate.get<GoogleCalendarStatusResponse>(
      url
    );
    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

/**
 * Disconnette Google Calendar dall'account utente
 */
export const disconnectGoogleCalendar = async (): Promise<void> => {
  try {
    const url = appendHsOwnerIdIfPresentAndAdmin(endpoints.googleCalendar.disconnect);
    await axiosApiInstancePrivate.delete(url);
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};

/**
 * Recupera gli eventi da Google Calendar in un intervallo di date
 * @param params Parametri con startDate e endDate
 * @returns Lista degli eventi Google Calendar
 */
export const getGoogleCalendarEvents = async (
  params: GetGoogleCalendarEventsParams
): Promise<GoogleCalendarEventsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);

    let url = `${endpoints.googleCalendar.events}?${queryParams.toString()}`;

    // Add hsOwnerId if present and user is admin or has team
    url = appendHsOwnerIdIfPresentAndAdmin(url);

    const response = await axiosApiInstancePrivate.get<GoogleCalendarEventsResponse>(
      url
    );
    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
};
